import { contrast, generateScale, SCALES, SHADES, type Foundations, type ScaleName, type Shade } from "./colors"

export const MODES = ["light", "dark", "high-contrast"] as const
export type Mode = (typeof MODES)[number]
export const MODE_LABELS: Record<Mode, string> = { light: "Claro", dark: "Oscuro", "high-contrast": "Alto contraste" }
export function foundationTokens(foundations: Foundations) {
  const result: Record<string,string> = { "absolute-white": "#FFFFFF", "absolute-black": "#000000" }
  for (const name of SCALES) {
    const scale = generateScale(foundations[name])
    for (const shade of SHADES) result[`${name}-${shade}`] = scale[shade]
  }
  return result
}
/** Mappings reference foundations; no component owns theme-specific colors. */
export function semanticTokens(foundations: Foundations, mode: Mode): Record<string,string> {
  const primitives = foundationTokens(foundations)
  const dark = mode !== "light", hc = mode === "high-contrast"
  const ref = (name: string) => `var(--${name})`
  const bgName = hc ? "absolute-black" : dark ? "neutral-950" : "absolute-white"
  const fgName = dark ? "absolute-white" : "absolute-black"
  const bg = primitives[bgName]
  const readable = (scale: ScaleName, preferred: Shade, min = 4.5) => {
    const order = [preferred, ...SHADES.filter(s=>s!==preferred).sort((a,b)=>Math.abs(a-preferred)-Math.abs(b-preferred))]
    return order.map(s=>`${scale}-${s}`).find(key=>contrast(primitives[key],bg)>=min) ?? fgName
  }
  const on = (token: string) => ref(contrast(primitives[token], "#FFFFFF") >= contrast(primitives[token],"#000000") ? "absolute-white" : "absolute-black")
  const primary = readable("primary", dark ? 300 : 500, hc ? 7 : 3)
  const secondary = readable("secondary", dark ? 300 : 100, 3)
  const danger = readable("danger", dark ? 300 : 600, hc ? 7 : 4.5)
  const border = readable("neutral", dark ? 700 : 300, hc ? 7 : 3)
  const mutedFg = readable("neutral", dark ? 300 : 600, hc ? 7 : 4.5)
  const surface = hc ? bgName : dark ? "neutral-900" : "neutral-50"
  const tokens: Record<string,string> = {
    background: ref(bgName), foreground: ref(fgName), card: ref(hc ? bgName : dark ? "neutral-900" : "absolute-white"), "card-foreground": ref(fgName),
    popover: ref(hc ? bgName : dark ? "neutral-900" : "absolute-white"), "popover-foreground": ref(fgName),
    primary: ref(primary), "primary-foreground": on(primary), secondary: ref(secondary), "secondary-foreground": on(secondary),
    muted: ref(surface), "muted-foreground": ref(mutedFg), accent: ref(surface), "accent-foreground": ref(fgName),
    destructive: ref(danger), "destructive-foreground": on(danger), border: ref(border), input: ref(border), ring: ref(hc ? "absolute-white" : primary),
    surface: ref(surface), "surface-foreground": ref(fgName), sidebar: ref(surface), "sidebar-foreground": ref(fgName),
    "sidebar-primary": ref(primary), "sidebar-primary-foreground": on(primary), "sidebar-accent": ref(surface), "sidebar-accent-foreground": ref(fgName), "sidebar-border": ref(border), "sidebar-ring": ref(primary),
  }
  for (const name of ["warning","success"] as const) {
    const token = readable(name, dark ? 300 : 700, hc ? 7 : 4.5)
    tokens[name] = ref(token); tokens[`${name}-foreground`] = on(token)
  }
  for (const [index, name] of (["support-1", "support-2", "support-3"] as const).entries()) {
    const token = readable(name, dark ? 300 : 600, hc ? 7 : 3)
    tokens[name] = ref(token)
    tokens[`${name}-foreground`] = on(token)
    tokens[`chart-${index + 3}`] = ref(token)
  }
  tokens["chart-1"] = ref(primary)
  tokens["chart-2"] = ref(secondary)
  return tokens
}
export function tokensCss(foundations: Foundations, includeMappings = true) {
  const rule = (selector: string, tokens: Record<string,string>) => `${selector} {\n${Object.entries(tokens).map(([k,v])=>`  --${k}: ${v};`).join("\n")}\n}`
  return ["/* Nebius OS foundations and semantic themes — MIT */",rule(":root",foundationTokens(foundations)),includeMappings ? rule(":root, .light",semanticTokens(foundations,"light")) : "",includeMappings ? rule(".dark",semanticTokens(foundations,"dark")) : "",rule(".high-contrast",semanticTokens(foundations,"high-contrast")),
    ".high-contrast :focus-visible { outline: 3px solid var(--ring); outline-offset: 3px; }",
    ".high-contrast [aria-invalid=true] { border-style: dashed; border-width: 2px; }",
    "@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; } }"].join("\n\n")
}
